// siteClassification.js — vocabularies and helpers for project site details.
//
// Three site attributes are captured per project (see the 20260826000001
// migration): elevation (metres), a Köppen–Geiger climate zone, and one or more
// site-setting descriptors.
//
// Köppen zones and site types are fixed vocabularies, rendered as a dropdown and
// a multi-select respectively. `suggestKoppen` proposes a climate code from a
// site's coordinates (and elevation, when known) so a human only has to confirm
// or correct it — Google Maps returns elevation but has no climate API, so this
// is a self-contained, deliberately approximate classifier, not an authority.

// ════════════════════════════════════════════════════════════════════════════
// SITE TYPES (multi-select)
// ════════════════════════════════════════════════════════════════════════════
// Stored as text[] in projects.site_types. `value` is the stored token,
// `label` is the human label.
export const SITE_TYPES = [
  { value: 'urban',        label: 'Urban' },
  { value: 'suburban',     label: 'Suburban' },
  { value: 'semi_urban',   label: 'Semi-urban' },
  { value: 'rural',        label: 'Rural' },
  { value: 'coastal',      label: 'Coastal / Seaside' },
  { value: 'waterfront',   label: 'Waterfront (river / lake)' },
  { value: 'forest',       label: 'Forest / Woodland' },
  { value: 'jungle',       label: 'Jungle' },
  { value: 'hillside',     label: 'Hillside / Mountain' },
  { value: 'desert',       label: 'Desert / Arid' },
  { value: 'agricultural', label: 'Agricultural / Farmland' },
  { value: 'plantation',   label: 'Plantation / Orchard' },
  { value: 'wetland',      label: 'Wetland' },
  { value: 'island',       label: 'Island' },
];

const SITE_TYPE_LABEL = Object.fromEntries(SITE_TYPES.map(t => [t.value, t.label]));

export function siteTypeLabel(value) {
  return SITE_TYPE_LABEL[value] || value;
}

// ════════════════════════════════════════════════════════════════════════════
// KÖPPEN–GEIGER CLIMATE ZONES (single-select dropdown)
// ════════════════════════════════════════════════════════════════════════════
// Full standard set, grouped by main climate group. `code` is stored in
// projects.climate_zone.
export const KOPPEN_ZONES = [
  // A — Tropical
  { code: 'Af',  group: 'A', label: 'Af — Tropical rainforest' },
  { code: 'Am',  group: 'A', label: 'Am — Tropical monsoon' },
  { code: 'Aw',  group: 'A', label: 'Aw — Tropical savanna (dry winter)' },
  { code: 'As',  group: 'A', label: 'As — Tropical savanna (dry summer)' },
  // B — Arid
  { code: 'BWh', group: 'B', label: 'BWh — Hot desert' },
  { code: 'BWk', group: 'B', label: 'BWk — Cold desert' },
  { code: 'BSh', group: 'B', label: 'BSh — Hot semi-arid (steppe)' },
  { code: 'BSk', group: 'B', label: 'BSk — Cold semi-arid (steppe)' },
  // C — Temperate
  { code: 'Cfa', group: 'C', label: 'Cfa — Humid subtropical' },
  { code: 'Cfb', group: 'C', label: 'Cfb — Oceanic / temperate' },
  { code: 'Cfc', group: 'C', label: 'Cfc — Subpolar oceanic' },
  { code: 'Cwa', group: 'C', label: 'Cwa — Humid subtropical (dry winter)' },
  { code: 'Cwb', group: 'C', label: 'Cwb — Subtropical highland (dry winter)' },
  { code: 'Cwc', group: 'C', label: 'Cwc — Cold subtropical highland' },
  { code: 'Csa', group: 'C', label: 'Csa — Hot-summer Mediterranean' },
  { code: 'Csb', group: 'C', label: 'Csb — Warm-summer Mediterranean' },
  { code: 'Csc', group: 'C', label: 'Csc — Cold-summer Mediterranean' },
  // D — Continental
  { code: 'Dfa', group: 'D', label: 'Dfa — Hot-summer continental' },
  { code: 'Dfb', group: 'D', label: 'Dfb — Warm-summer continental' },
  { code: 'Dfc', group: 'D', label: 'Dfc — Subarctic' },
  { code: 'Dfd', group: 'D', label: 'Dfd — Extremely cold subarctic' },
  { code: 'Dwa', group: 'D', label: 'Dwa — Continental, dry winter (hot summer)' },
  { code: 'Dwb', group: 'D', label: 'Dwb — Continental, dry winter (warm summer)' },
  { code: 'Dwc', group: 'D', label: 'Dwc — Subarctic, dry winter' },
  { code: 'Dsa', group: 'D', label: 'Dsa — Continental, dry summer (hot)' },
  { code: 'Dsb', group: 'D', label: 'Dsb — Continental, dry summer (warm)' },
  // E — Polar / Alpine
  { code: 'ET',  group: 'E', label: 'ET — Tundra / alpine' },
  { code: 'EF',  group: 'E', label: 'EF — Ice cap' },
];

const KOPPEN_LABEL = Object.fromEntries(KOPPEN_ZONES.map(z => [z.code, z.label]));

// Full descriptive label for a stored code (falls back to the bare code).
export function koppenLabel(code) {
  return code ? (KOPPEN_LABEL[code] || code) : '';
}

// ════════════════════════════════════════════════════════════════════════════
// COORDINATE → KÖPPEN SUGGESTION
// ════════════════════════════════════════════════════════════════════════════
// Approximate, confirm-by-human. Google Maps has no climate service, so this
// classifier leans on the firm's real footprint (India + NW Europe) with a
// latitude fallback elsewhere, and an elevation override for high-altitude sites.
// Returns { code, basis } — basis is a short human explanation — or null when
// there is nothing to go on.
const inBox = (lat, lng, [latMin, latMax, lngMin, lngMax]) =>
  lat >= latMin && lat <= latMax && lng >= lngMin && lng <= lngMax;

export function suggestKoppen(lat, lng, elevationM = null) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const absLat = Math.abs(lat);
  const elev = Number.isFinite(elevationM) ? elevationM : null;

  // ── Elevation override: very high sites are alpine/ice regardless of latitude
  if (elev != null && elev >= 4500) return { code: 'EF', basis: `Elevation ~${Math.round(elev)} m — permanent ice / high alpine` };
  if (elev != null && elev >= 3000) return { code: 'ET', basis: `Elevation ~${Math.round(elev)} m — alpine tundra` };

  // ── India subcontinent — the bulk of the firm's sites
  if (inBox(lat, lng, [6, 37, 68, 98])) {
    // Himalayan / high hill stations (Himachal, Uttarakhand hills) → highland
    if (elev != null && elev >= 1200 && lat >= 27) return { code: 'Cwb', basis: 'Himalayan hill site — subtropical highland' };
    if (elev != null && elev >= 1500) return { code: 'Cwb', basis: `Highland site ~${Math.round(elev)} m — subtropical highland` };
    // NW arid zone (Rajasthan, Kutch, arid Gujarat)
    if (inBox(lat, lng, [23, 30, 68, 75.5])) return { code: 'BWh', basis: 'North-west India arid belt — hot desert' };
    // West coast monsoon strip (Konkan, Goa, Kerala) — very wet SW monsoon
    if (lng < 74.5 && absLat <= 20) return { code: 'Am', basis: 'West-coast monsoon belt — tropical monsoon' };
    // Gangetic / northern plains (humid subtropical, dry winter)
    if (lat >= 24) return { code: 'Cwa', basis: 'Northern plains — humid subtropical, dry winter' };
    // Peninsular lowland default — tropical savanna
    return { code: 'Aw', basis: 'Peninsular India lowland — tropical savanna' };
  }

  // ── NW & Central Europe (incl. the Rotterdam office region) → oceanic
  if (inBox(lat, lng, [44, 60, -10, 20])) {
    if (elev != null && elev >= 1000) return { code: 'Dfb', basis: `Alpine/upland Europe ~${Math.round(elev)} m` };
    return { code: 'Cfb', basis: 'North-west / central Europe — oceanic temperate' };
  }
  // ── Mediterranean basin → dry-summer temperate
  if (inBox(lat, lng, [30, 45, -10, 40])) return { code: 'Csa', basis: 'Mediterranean basin — dry-summer temperate' };

  // ── Latitude fallback for anywhere else
  if (absLat < 10)  return { code: 'Af',  basis: `Equatorial latitude ~${absLat.toFixed(0)}°` };
  if (absLat < 23.5) return { code: 'Aw', basis: `Tropical latitude ~${absLat.toFixed(0)}°` };
  if (absLat < 35)  return { code: 'Cfa', basis: `Subtropical latitude ~${absLat.toFixed(0)}°` };
  if (absLat < 55)  return { code: 'Cfb', basis: `Temperate latitude ~${absLat.toFixed(0)}°` };
  if (absLat < 66)  return { code: 'Dfb', basis: `Continental latitude ~${absLat.toFixed(0)}°` };
  return { code: 'ET', basis: `Polar latitude ~${absLat.toFixed(0)}°` };
}
